import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FreshnessGauge } from "@/components/FreshnessGauge";
import { CertifiedBadge } from "@/components/CertifiedBadge";
import SharePropertyMenu from "@/components/SharePropertyMenu";
import ViewersTicker from "@/components/ViewersTicker";
import InstagramSquare from "@/components/InstagramSquare";
import { Button } from "@/components/ui/button";
import { Activity, Wind, Droplet, Sparkles, Flame, MapPin, Heart, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PropertyReviews from "@/components/PropertyReviews";

const CHECKS = [
  { key: "freshness_monitoring", label: "Freshness Monitoring", icon: Activity },
  { key: "air_quality",          label: "Air Quality",          icon: Wind },
  { key: "smoke_detection",      label: "Smoke Detection",      icon: Flame },
  { key: "humidity",             label: "Humidity",             icon: Droplet },
  { key: "cleaning_verified",    label: "Cleaning Verified",    icon: Sparkles },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  const favorite = async () => {
    if (!user || user.role !== "guest") return toast.info("Sign in as a guest to save favorites");
    try {
      await api.post(`/properties/${id}/favorite`);
      toast.success("Favorite updated");
    } catch { toast.error("Could not update favorite"); }
  };

  if (p === null) return <div className="p-10 text-slate-500">Loading…</div>;
  if (p === false) return <div className="p-10 text-slate-500">Property not found.</div>;

  const photos = p.photos?.length ? p.photos : ["https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=1600"];

  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            {p.status === "certified" && <CertifiedBadge tier={p.certification_tier || "standard"} compact />}
            <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-[#0A1128]" data-testid="detail-name">{p.name}</h1>
            <div className="mt-2 flex items-center gap-2 text-slate-600 text-sm">
              <MapPin className="w-4 h-4" /> {p.address}, {p.city}, {p.country}
            </div>
            <ViewersTicker propertyId={p.id} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={favorite} data-testid="detail-favorite" className="rounded-full">
              <Heart className="w-4 h-4 mr-1" /> Save
            </Button>
            <SharePropertyMenu property={p} />
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 aspect-[16/10] rounded-3xl overflow-hidden">
            <img src={photos[0]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
            {photos.slice(1, 3).map((src) => (
              <div key={src} className="aspect-[16/10] rounded-3xl overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            {photos.length < 2 && (
              <div className="aspect-[16/10] rounded-3xl bg-gradient-to-br from-emerald-100 to-[#F9F3E5] flex items-center justify-center text-slate-400 text-sm">
                More photos coming soon
              </div>
            )}
          </div>
        </div>

        {/* Content 2-column */}
        <div className="mt-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">About this stay</div>
            <p className="mt-3 text-slate-700 leading-relaxed text-lg">{p.description || "This property is monitored under the Guest Smell™ standard."}</p>

            <div className="mt-10 text-xs tracking-[0.28em] uppercase text-emerald-700 font-semibold">Verified checklist</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {CHECKS.map((c) => {
                const v = p.metrics?.[c.key] ?? 0;
                return (
                  <div key={c.key} data-testid={`check-${c.key}`} className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <c.icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#0A1128]">{c.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Verified · {v}/100</div>
                    </div>
                    <div className="w-14 text-right font-serif text-2xl text-[#0A1128]">{v}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 rounded-3xl p-6 border-2 border-dashed border-[#D4AF37]/50 bg-[#F9F3E5]/60">
              <div className="text-xs tracking-[0.28em] uppercase text-[#0A1128] font-semibold">Coming Soon</div>
              <h3 className="mt-2 font-serif text-2xl text-[#0A1128]">Experience This Stay™ · Scent Preview</h3>
              <p className="mt-2 text-slate-600 text-sm">Sniff the room before you book. Cedar, linen, sea air — our upcoming scent-signature preview will let you experience the space in advance.</p>
            </div>

            <div className="mt-10">
              <InstagramSquare property={p} tier={p.certification_tier || "premium"} />
            </div>

            <PropertyReviews propertyId={p.id} propertyStatus={p.status} />
          </div>

          <aside className="md:col-span-4">
            <div className="sticky top-24">
              <div className="bg-white rounded-3xl border border-slate-200 p-6">
                <FreshnessGauge score={p.freshness_score || 0} />
                {p.price_per_night && (
                  <div className="mt-4 text-center">
                    <div className="font-serif text-3xl text-[#0A1128]">${p.price_per_night}<span className="text-sm text-slate-500 font-sans"> / night</span></div>
                  </div>
                )}
                <a href={p.booking_url || "#"} target="_blank" rel="noreferrer" data-testid="book-now">
                  <Button className="w-full mt-5 h-12 rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white">
                    Book on {p.booking_platform?.[0]?.toUpperCase() + p.booking_platform?.slice(1)} <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                {p.certification_code && (
                  <Link to={`/certification/${p.certification_code}`} className="mt-3 block text-center text-sm text-emerald-700 hover:underline" data-testid="view-cert-link">
                    View public certification →
                  </Link>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
